import { VirtualField, KeywordFieldReferences,VirtualFieldGroup } from './../api/data-model';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of, from } from 'rxjs';
import { map, switchMap, catchError, mergeMap, toArray, finalize } from 'rxjs/operators';
import { BACKEND_URL } from '../app.config';
import { ListResult, QuestionnaireUsageResult } from '../api/data-model';
import { LookupService } from './lookup.service';
import { QuestionnaireTemplateService } from './questionnaire.template.service';
import { FormDesignService } from './form-design.service';
import { EntityType } from '../api/enum/entity-type';
@Injectable({
  providedIn: 'root'
})
export class VirtualFieldService {

  constructor(
    private readonly http: HttpClient,
    @Inject(BACKEND_URL) private readonly baseUrl: string,
    private readonly lookupService: LookupService,
    private readonly questionnaireTemplateService: QuestionnaireTemplateService,
    private readonly formDesignService: FormDesignService
  ) {

  }

  getList(entityId: string, entityType: number, sorting: string, skipCount: number, maxResultCount: number): Observable<ListResult<VirtualField>> {
    return this.http.get<ListResult<VirtualField>>(this.baseUrl + '/api/app/virtual-field', {
      params: {
        entityId,
        entityType,
        sorting,
        skipCount,
        maxResultCount,
      },
    });
  }

  getVirtualField(id: string): Observable<VirtualField> {
    return this.http.get<VirtualField>(this.baseUrl + '/api/app/virtual-field/' + id);
  }

  addVirtualField(virtualField: VirtualField): Observable<VirtualField> {
    return this.http.post<VirtualField>(`${this.baseUrl}/api/app/virtual-field`, virtualField);
  }

  updateVirtualField(id: string, virtualField: VirtualField): Observable<VirtualField> {
    return this.http.put<VirtualField>(`${this.baseUrl}/api/app/virtual-field/${id}`, virtualField);
  }


  deleteVirtualField(id: string): Observable<void> {
    return this.http.delete<void>(this.baseUrl + '/api/app/virtual-field/' + id);
  }

  getVirtualFieldTypes(): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/api/app/virtual-field/field-types`);
  }

  getGroupedVirtualFields(entityId: string, entityType: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/api/app/virtual-field/field-groups/` + entityId, {
      params: {
        entityId,
        entityType,
      },
    });
  }

  updateGroupedVirtualFields(entityId: string, entityType: number, groups: VirtualFieldGroup[]): Observable<void> {
        return this.http.put<void>(`${this.baseUrl}/api/app/virtual-field/field-groups/${entityId}`, groups, {
            params: {
                entityId,
                entityType: entityType.toString(),
            }
        });
    }


  //     getGroupedVirtualFields(entityId: string, entityType: number): Observable<any[]> {
  //     return this.http.get<any[]>(`${this.baseUrl}/api/app/virtual-field/field-groups/`, {
  //         params: {
  //             entityId,
  //             entityType,
  //         },
  //     });
  // }

  /**
   * Get virtual field references by keyword
   * @param keyword - The keyword to search for references
   * @returns Observable with virtual field references including Virtual Fields, Form Fields, Form Designs, and Workflow Action
   */
  getReferences(keyword: string): Observable<KeywordFieldReferences> {
    return this.http.get<KeywordFieldReferences>(`${this.baseUrl}/api/app/virtual-field/references`, {
      params: { keyword }
    });
  }

  /**
   * Get questionnaires that use a specific Virtual Field and return questionnaire details
   * @param virtualFieldName - The name of the VirtualField to check
   * @param onProgress - Optional callback to report progress
   * @returns Observable with array of questionnaire objects containing name and formDesign
   */
  getQuestionnaireUsage(
    virtualFieldName: string,
    onProgress?: (status: string) => void
  ): Observable<Array<QuestionnaireUsageResult>> {
    if (onProgress) {
      onProgress('Checking questionnaire usage...');
    }

    return this.questionnaireTemplateService.getList('', 0, 1000).pipe(
      switchMap((questionnaires) => {
        if (!questionnaires.items || questionnaires.items.length === 0) {
          return of([]);
        }

        const total = questionnaires.items.length;
        let completed = 0;
        const pattern = new RegExp(`"${virtualFieldName}"`);

        return from(questionnaires.items).pipe(
          mergeMap(
            (questionnaire) => {
              return this.formDesignService.query(EntityType.Questionnaire, questionnaire.id, false).pipe(
                map((formDesign) => {
                  if (formDesign?.data && pattern.test(formDesign.data)) {
                    return {
                      name: questionnaire.name,
                      audience: questionnaire.audience,
                      formDesign: formDesign
                    };
                  }

                  return null;
                }),
                catchError(() => {
                  return of(null);
                }),
                finalize(() => {
                  completed++;
                  if (onProgress && completed % 5 === 0) {
                    const percent = Math.round((completed / total) * 100);
                    onProgress(`Checking Questionnaires... ${completed}/${total} (${percent}%)`);
                  }
                })
              );
            },
            10 // Concurrency limit of 5
          ),
          toArray(),
          map((results) => results.filter((item): item is QuestionnaireUsageResult => item !== null))
        );
      })
    );
  }

  copyVirtualField(virtualField: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/api/app/virtual-field/copy`, virtualField);
  }

}
